import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
//
import Layout from '../../components/Layout';
import { Title } from '../../components/ui';
import {
  getAssignedCustomers,
  impersonateCustomer,
  sendWelcomeMessage,
} from '../../api/services/repService';
import { initUserInfo, setUserInfo } from '../../redux/userInfo';
import { startImpersonation } from '../../utils/impersonation';

const Container = styled.div`
  display: flex;
  justify-content: center;
  padding: 3rem 0;
  width: 100%;
`;

const Content = styled.div`
  width: 90%;
  max-width: 1300px;
`;

const SearchRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin: 20px 0;
  align-items: flex-end;
`;

const SearchField = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 0.85rem;
  color: #555;

  input {
    margin-top: 4px;
    padding: 8px 10px;
    border: 1px solid #ccc;
    border-radius: 4px;
    width: 180px;
  }
`;

const SearchButton = styled.button`
  background: ${(props) => props.theme.colors.primary || '#2b3990'};
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 10px 24px;
  font-weight: bold;
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;

  th {
    background: #f6f6f6;
    text-align: left;
    padding: 10px 12px;
    border-bottom: 2px solid #ddd;
    white-space: nowrap;
  }

  td {
    padding: 10px 12px;
    border-bottom: 1px solid #eee;
    vertical-align: middle;
  }

  tr:hover td {
    background: #fafafa;
  }
`;

const ActionButton = styled.button`
  display: block;
  width: 160px;
  margin: 3px 0;
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  font-size: 0.8rem;
  text-align: center;

  &:hover {
    background: #f0f0f0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const ShopButton = styled(ActionButton)`
  width: 80px;
`;

const StatusText = styled.div`
  text-align: center;
  padding: 2rem;
  color: #777;
`;

export default function RepCartsPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.userInfo);

  const [filters, setFilters] = useState({
    email: '',
    firstname: '',
    lastname: '',
    phone: '',
    account: '',
  });
  const [customerList, setCustomerList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const isEmployee =
    String(userInfo.role || '')
      .trim()
      .toLowerCase() === 'employee';

  useEffect(() => {
    // 영업사원이 아니면 접근 차단
    if (!isEmployee) {
      navigate('/');
      return;
    }
    search();
  }, []);

  const search = async () => {
    setLoading(true);
    setMessage('');
    try {
      const params = {};
      Object.keys(filters).forEach((key) => {
        if (filters[key].trim()) params[key] = filters[key].trim();
      });
      const result = await getAssignedCustomers(params);
      setCustomerList(result.customerList || []);
    } catch (e) {
      setMessage('Failed to load customer list.');
      setCustomerList([]);
    }
    setLoading(false);
  };

  // 고객 세션으로 전환 후 target 경로로 이동
  const impersonate = async (row, targetPath) => {
    try {
      const result = await impersonateCustomer(row.userId);
      if (!result.is_success) {
        window.alert('Failed to start customer session: ' + result.message);
        return;
      }
      // 현재 rep 세션 백업 + 고객 토큰 적용
      const ok = startImpersonation(userInfo, result.user.token);
      if (!ok) {
        window.alert('Already in an impersonated session.');
        return;
      }
      dispatch(initUserInfo());
      dispatch(
        setUserInfo({
          ...result.user,
          loginDt: new Date().toDateString(),
        }),
      );
      navigate(targetPath);
    } catch (e) {
      window.alert('Failed to start customer session.');
    }
  };

  const onSendWelcome = async (row) => {
    if (
      !window.confirm(`Send welcome (create password) email to ${row.email}?`)
    ) {
      return;
    }
    try {
      const result = await sendWelcomeMessage(row.userId);
      if (result.is_success) {
        window.alert(`Welcome email sent to ${result.email}`);
      } else {
        window.alert('Failed to send: ' + result.message);
      }
    } catch (e) {
      window.alert('Failed to send welcome email.');
    }
  };

  const onFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const searchFields = [
    ['email', 'Search Email'],
    ['firstname', 'Search First Name'],
    ['lastname', 'Search Last Name'],
    ['phone', 'Search Phone'],
    ['account', 'Search Account'],
  ];

  return (
    <Layout>
      <Container>
        <Content>
          <Title style={{ textAlign: 'center' }}>
            Reps Assigned Customer Carts
          </Title>

          <SearchRow>
            {searchFields.map(([key, label]) => (
              <SearchField key={key}>
                {label}
                <input
                  value={filters[key]}
                  onChange={(e) => onFilterChange(key, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') search();
                  }}
                />
              </SearchField>
            ))}
            <SearchButton onClick={search}>SEARCH</SearchButton>
          </SearchRow>

          <TableWrapper>
            <StyledTable>
              <thead>
                <tr>
                  <th>Cart</th>
                  <th>Shop</th>
                  <th>Company</th>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>BP Number</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Active Cart</th>
                </tr>
              </thead>
              <tbody>
                {customerList.map((row) => (
                  <tr key={row.idUser}>
                    <td>
                      <ActionButton onClick={() => impersonate(row, '/cart')}>
                        View Cart
                      </ActionButton>
                      <ActionButton onClick={() => onSendWelcome(row)}>
                        Send Welcome Message
                      </ActionButton>
                    </td>
                    <td>
                      <ShopButton onClick={() => impersonate(row, '/')}>
                        Shop
                      </ShopButton>
                    </td>
                    <td>{row.companyNm}</td>
                    <td>{row.firstname}</td>
                    <td>{row.lastname}</td>
                    <td>{row.bpId}</td>
                    <td>{row.email}</td>
                    <td>{row.tel}</td>
                    <td>{row.activeCart === 1 ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </StyledTable>
            {loading && <StatusText>Loading...</StatusText>}
            {!loading && customerList.length === 0 && (
              <StatusText>
                {message || 'No assigned customers found.'}
              </StatusText>
            )}
          </TableWrapper>
        </Content>
      </Container>
    </Layout>
  );
}
